/**
 * Add Bank Account Modal
 * @description This component adds new bank account
 * @author Tirumal Rao
 */
import React, { Component } from 'react';
import { Modal } from 'semantic-ui-react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Form, Button, Message } from 'semantic-ui-react';
import { reduxForm, Field } from 'redux-form';
import {
  composeValidators,
  combineValidators,
  isRequired,
  isNumeric,
  isAlphaNumeric,
  hasLengthGreaterThan,
  hasLengthLessThan,
  matchesPattern
} from 'revalidate';
import { closeModal } from '../../modals/modalActions';
import TextInput from '../../../app/common/Form/TextInput';
import BanksDropdown from '../../SearchableDropDowns/BanksDropdown';
import { openModal } from '../../modals/modalActions';
import API from '../../../APIconfig';
import { accountAdded, deleteOTP, generateOTP } from '../accountActions';

/**
 * ========================= CONFIGURATIONS START ===================
 */

const formName = 'addBankAccount';

// Redux actions
const actions = {
  accountAdded,
  closeModal,
  deleteOTP,
  generateOTP,
  openModal,
};

// Redux state
const mapState = state => ({
  auth: state.auth,
  async: state.async,
  bank: state.bank,
});

/**
 * ========================= CONFIGURATIONS ENDS =====================
 */

/**
 * ========================== VALIDATIONS ============================
 */

const validate = combineValidators({
  // Account Number
  accountNumber: composeValidators(
    isRequired({ message: 'Account Number is required' }),
    isNumeric({ message: 'Account number should be numeric' }),
    hasLengthGreaterThan(8)({
      message: 'Account number length should be 9 digits to 18 digits long'
    }),
    hasLengthLessThan(19)({
      message: 'Account number length should be 9 digits to 18 digits long'
    })
  )(),

  // IFSC Code
  ifsc: composeValidators(
    isRequired({ message: 'IFSC code is required' }),
    isAlphaNumeric({ message: 'IFSC code should be alphanumeric' }),
    hasLengthGreaterThan(10)({
      message: 'IFSC code should be 11 characters long'
    }),
    hasLengthLessThan(12)({
      message: 'IFSC code should be 11 characters long'
    }),
    matchesPattern(/^[A-Za-z]{4}[0][A-Za-z0-9]{6}$/)({ message: 'Invalid IFSC Code' })
  )(),

  // Short name OR Account holder name
  shortName: composeValidators(
    isRequired({ message: 'This field is required' }),
    matchesPattern(/^[a-zA-Z ]+$/)({message: 'Account holder name should be alphabetic'}),
    hasLengthLessThan(86)({ message: 'Maximum length allowed is 85' })
  )(),

  // Bank name
  bankName: isRequired({ message: 'Bank name is required' })
});

/**
 * ========================== VALIDATIONS ENDS ======================
 */

class AddBankAccountModal extends Component {
  static propTypes = {
    accountAdded: PropTypes.func.isRequired,
    async: PropTypes.object.isRequired,
    auth: PropTypes.object.isRequired,
    bank: PropTypes.object,
    closeModal: PropTypes.func.isRequired,
    deleteOTP: PropTypes.func.isRequired,
    generateOTP: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired
  };

  // init state
  state = {};

  addBankAccount = values => {
    const { accountAdded, auth, generateOTP, openModal } = this.props;

    generateOTP(
      API.bank.generateOTPforAddBank,
      { request: values },
      auth.csrfRequestIdentifier,
      () => {
        // Construct payload
        const payload = {
          values: { request: values },
          csrfRequestIdentifier: auth.csrfRequestIdentifier,
          forwardAction: accountAdded,
          api: {
            generate: API.bank.generateOTPforAddBank,
            verify: API.bank.verifyOTPforAddBank,
          }
        };

        // Request to open OTP modal
        openModal('OTPModal', { payload });
      }
    );

  };

  onKeyPress = event => {
    if (event.key === 'Enter') {
      // This stops the form from being submitted
      event.preventDefault();
    }
  };

  render() {
    const {
      async,
      bank,
      closeModal,
      handleSubmit,
      invalid,
      submitting,
      pristine
    } = this.props;
    return (
      <Modal closeIcon={true} size="tiny" open={true} onClose={closeModal}>
        <Modal.Header>Add Bank Account</Modal.Header>
        <Modal.Content>
          <Modal.Description>
            <Form
              onSubmit={handleSubmit(this.addBankAccount)}
              onKeyPress={this.onKeyPress}
              loading={async.loading && async.key === 'generateOTP'}
            >
              <Field
                name="accountNumber"
                placeholder="Account Number"
                maxLength={18}
                component={TextInput}
                type="tel"
                allowonlynumber="true"
              />
              <BanksDropdown name="bankName" />
              <Field
                name="ifsc"
                placeholder="IFSC Code"
                type="text"
                maxLength={11}
                component={TextInput}
              />
              <Field
                name="shortName"
                placeholder="Account Holder Name"
                type="text"
                maxLength={85}
                component={TextInput}
              />
              {bank && bank.otp && bank.otp.error && bank.otp.error.errorMessage && (
                <Message negative>
                  <Message.Header>Error: {bank.otp.error.errorMessage}</Message.Header>
                </Message>
              )}
              <Button className="fcbtn fccancelbtn small" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                className="fcbtn fcprimary small"
                disabled={invalid || submitting || pristine}
                type="submit"
              >
                Save
              </Button>
            </Form>
          </Modal.Description>
        </Modal.Content>
      </Modal>
    );
  }
}

// Redux connect and reduxForm HOC on statefull component 'AddBankAccountModal'
export default connect(
  mapState,
  actions
)(
  reduxForm({
    form: formName,
    destroyOnUnmount: true,
    enableReinitialize: true,
    validate
  })(AddBankAccountModal)
);
